import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, Package, Plus, LogOut, Search, TrendingUp, Users, 
  MapPin, Heart, Edit, Trash2, X, Upload, Image, Tag, FileText, 
  CheckCircle, Loader2, Save, ArrowLeft, Rocket, 
  BarChart3, PieChart, Activity, Bell, Menu, Sparkles,
  Calendar, Eye, Download, Clock,
  RefreshCw, FileDown, CalendarClock, AlertTriangle,
  Grid, List, Settings, History, Video,
  Database, Shield, UserPlus, Key, FileSpreadsheet, 
  BellRing, Check, AlertCircle, CalendarCheck, ShieldAlert, Inbox,
  Star, Play, Link, GripVertical, Youtube
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RePieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { Toaster, toast } from 'sonner';
import AdminLoginPremium from './AdminLoginPremium';
import SecurityDashboard from './SecurityDashboard';
import SecuritySettingsPanel from './SecuritySettingsPanel';
import AdminDonations from './AdminDonations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PREDEFINED_TAGS = ['AÇÃO SOCIAL', 'OUTUBRO ROSA', 'DIA DAS CRIANÇAS', 'ESPORTE', 'PROTEÇÃO ANIMAL', 'NATAL SOLIDÁRIO', 'PÁSCOA', 'EDUCAÇÃO', 'SAÚDE', 'CAUSA ANIMAL'];
const MONTHS = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' }
];
const YEARS = Array.from({length: 5}, (_, i) => 2024 + i);

const URGENCY_CONFIG = {
  baixa: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', label: 'Baixa', dot: 'bg-emerald-400' },
  normal: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Normal', dot: 'bg-blue-400' },
  alta: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', label: 'Alta', dot: 'bg-amber-400' },
  urgente: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', label: 'Urgente', dot: 'bg-red-400' }
};

const STATUS_CONFIG = {
  rascunho: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Rascunho' },
  publicado: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', label: 'Publicado' },
  agendado: { bg: 'bg-purple-500/10', text: 'text-purple-400', label: 'Agendado' }
};

const ROLE_CONFIG = {
  super_admin: { bg: 'bg-red-500/10', text: 'text-red-400', label: 'Super Admin' },
  editor: { bg: 'bg-blue-500/10', text: 'text-blue-400', label: 'Editor' },
  viewer: { bg: 'bg-slate-500/10', text: 'text-slate-400', label: 'Visualizador' }
};

const CHART_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const NOTIF_ICONS = {
  'scheduled_publish': CalendarCheck,
  'login_alert': ShieldAlert,
  'backup_complete': Database,
  'system': Bell
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-[#050B14]/95 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-3 shadow-2xl">
        <p className="text-slate-400 text-xs mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-white font-semibold">{entry.name}: {entry.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (typeof value !== 'number') { setCount(value); return; }
    let start = 0;
    const timer = setInterval(() => {
      start += Math.ceil(value / 50);
      if (start >= value) { setCount(value); clearInterval(timer); }
      else { setCount(start); }
    }, Math.abs(Math.floor(duration / value)));
    return () => clearInterval(timer);
  }, [value, duration]);
  return <span>{typeof count === 'number' ? count.toLocaleString() : count}</span>;
};

// Preview Modal
const PreviewModal = ({ action, onClose }) => {
  if (!action) return null;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()} className="bg-[#0A0F1C] border border-white/10 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-white/[0.02] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Eye className="w-5 h-5 text-[#22c55e]" />
            <h3 className="text-white font-semibold">Preview - Como aparece no site</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div className="bg-gradient-to-b from-white/[0.02] to-transparent rounded-2xl">
            {action.media?.length > 0 ? (
              <div className="aspect-video relative overflow-hidden rounded-xl mb-6">
                {action.media[0].type === 'video' ? (
                  <video src={action.media[0].url} controls className="w-full h-full object-cover" />
                ) : (
                  <img src={action.media[0].thumbnail || action.media[0].url} alt={action.title} className="w-full h-full object-cover" />
                )}
                {action.media.length > 1 && <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-white text-sm">+{action.media.length - 1} mídia</div>}
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-[#22c55e]/10 to-transparent rounded-xl mb-6 flex items-center justify-center"><Image className="w-20 h-20 text-slate-600" /></div>
            )}
            <div className="space-y-4">
              {action.tags?.length > 0 && <div className="flex flex-wrap gap-2">{action.tags.map(tag => <span key={tag} className="px-3 py-1 bg-[#22c55e]/10 text-[#22c55e] text-xs font-medium rounded-full">{tag}</span>)}</div>}
              <h2 className="text-3xl font-bold text-white">{action.title}</h2>
              <p className="text-[#22c55e] font-medium">{action.subtitle || 'Parceria Viva Impact e WG'}</p>
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-white/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-blue-400" /></div>
                  <div><p className="text-xs text-slate-500 uppercase">Localização</p><p className="text-white font-medium">{action.location}</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-pink-500/10 flex items-center justify-center"><Heart className="w-5 h-5 text-pink-400" /></div>
                  <div><p className="text-xs text-slate-500 uppercase">Beneficiados</p><p className="text-white font-medium">{action.beneficiaries}</p></div>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{action.description}</p>
              <div className="flex items-center gap-3 pt-4">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${STATUS_CONFIG[action.status]?.bg} ${STATUS_CONFIG[action.status]?.text}`}>{STATUS_CONFIG[action.status]?.label || 'Publicado'}</span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${URGENCY_CONFIG[action.urgency]?.bg} ${URGENCY_CONFIG[action.urgency]?.text}`}>Urgência: {URGENCY_CONFIG[action.urgency]?.label || 'Normal'}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Notifications Panel
const NotificationsPanel = ({ notifications, unreadCount, onMarkRead, onMarkAllRead, onClose }) => (
  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
    className="absolute top-full right-0 mt-2 w-96 bg-[#0A0F1C] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
    <div className="bg-white/[0.02] border-b border-white/10 px-4 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <BellRing className="w-4 h-4 text-[#22c55e]" />
        <span className="text-white font-medium">Notificações</span>
        {unreadCount > 0 && <span className="px-2 py-0.5 bg-[#22c55e] text-[#050B14] text-xs font-bold rounded-full">{unreadCount}</span>}
      </div>
      {unreadCount > 0 && <button onClick={onMarkAllRead} className="text-xs text-[#22c55e] hover:underline">Marcar todas como lidas</button>}
    </div>
    <div className="max-h-96 overflow-y-auto">
      {notifications.length === 0 ? (
        <div className="py-8 text-center text-slate-500"><Bell className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>Nenhuma notificação</p></div>
      ) : (
        notifications.map(notif => {
          const Icon = NOTIF_ICONS[notif.type] || Bell;
          return (
            <div key={notif.id} className={`p-4 border-b border-white/[0.05] hover:bg-white/[0.02] transition-all ${!notif.read ? 'bg-[#22c55e]/5' : ''}`}>
              <div className="flex gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${notif.type === 'login_alert' ? 'bg-red-500/10' : 'bg-[#22c55e]/10'}`}>
                  <Icon className={`w-5 h-5 ${notif.type === 'login_alert' ? 'text-red-400' : 'text-[#22c55e]'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">{notif.title}</p>
                  <p className="text-slate-400 text-xs mt-1 line-clamp-2">{notif.message}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-slate-500 text-xs">{new Date(notif.created_at).toLocaleString('pt-BR')}</span>
                    {!notif.read && <button onClick={() => onMarkRead(notif.id)} className="text-xs text-[#22c55e] hover:underline flex items-center gap-1"><Check className="w-3 h-3" />Lida</button>}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  </motion.div>
);

// Create User Modal
const CreateUserModal = ({ onClose, onSuccess, getAuthHeader }) => {
  const [formData, setFormData] = useState({ username: '', email: '', role: 'editor' });
  const [loading, setLoading] = useState(false);
  const [tempPassword, setTempPassword] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (response.ok) {
        setTempPassword(data.temporary_password);
        toast.success('Usuário criado!');
      } else {
        toast.error(data.detail || 'Erro ao criar usuário');
      }
    } catch (err) {
      toast.error('Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()} className="bg-[#0A0F1C] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden">
        <div className="bg-white/[0.02] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Novo Usuário</h3></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {tempPassword ? (
          <div className="p-6">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
              <p className="text-emerald-400 font-medium mb-2">Usuário criado com sucesso!</p>
              <p className="text-slate-300 text-sm">Senha temporária:</p>
              <p className="text-white font-mono text-lg bg-black/30 rounded px-3 py-2 mt-2">{tempPassword}</p>
              <p className="text-amber-400 text-xs mt-2">⚠️ Salve esta senha! Ela não será mostrada novamente.</p>
            </div>
            <button onClick={() => { onSuccess(); onClose(); }} className="w-full px-4 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]">Fechar</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Username *</label>
              <input type="text" value={formData.username} onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))} required
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Email *</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} required
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Role</label>
              <select value={formData.role} onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                <option value="editor">Editor</option>
                <option value="viewer">Visualizador</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <button type="submit" disabled={loading} className="w-full px-4 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] disabled:opacity-50">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Criando...</> : 'Criar Usuário'}
            </button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
};

// Main Component
const AdminDashboardPremium = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showPreview, setShowPreview] = useState(false);
  const [previewAction, setPreviewAction] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const [actions, setActions] = useState([]);
  const [stats, setStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [tagStats, setTagStats] = useState([]);
  const [scheduledActions, setScheduledActions] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [systemSettings, setSystemSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const [donationStats, setDonationStats] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({ status: '', urgency: '', state: '' });
  const [sortBy, setSortBy] = useState('recent');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [editingAction, setEditingAction] = useState(null);
  const [formData, setFormData] = useState({
    title: '', subtitle: 'Parceria Viva Impact e WG', location: '', beneficiaries: '', description: '',
    tags: [], urgency: 'normal', status: 'publicado', scheduled_date: '', scheduled_time: '',
    month: '', year: new Date().getFullYear().toString(), customTag: ''
  });
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoLinkInput, setVideoLinkInput] = useState('');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('viva_admin_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('viva_admin_token');
    const expires = localStorage.getItem('viva_admin_expires');
    if (!token || !expires || Date.now() > parseInt(expires)) { setIsAuthenticated(false); setAuthLoading(false); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeader() });
      if (response.ok) {
        const user = await response.json();
        setCurrentUser(user);
        setIsAuthenticated(true);
        loadAllData();
      } else { handleLogout(); }
    } catch { handleLogout(); }
    finally { setAuthLoading(false); }
  };

  const handleLoginSuccess = () => { setIsAuthenticated(true); loadAllData(); checkAuth(); toast.success('Login realizado!'); };
  const handleLogout = async () => {
    try { await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', headers: getAuthHeader() }); } catch {}
    localStorage.removeItem('viva_admin_token'); localStorage.removeItem('viva_admin_expires');
    setIsAuthenticated(false); setCurrentUser(null);
  };

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([loadActions(), loadStats(), loadMonthlyStats(), loadTagStats(), loadPendingChanges(), loadScheduledActions(), loadNotifications(), loadDonationStats()]);
    setLoading(false);
  };

  const loadActions = async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append('search', debouncedSearch);
      if (filters.status) params.append('status', filters.status);
      if (filters.urgency) params.append('urgency', filters.urgency);
      if (filters.state) params.append('state', filters.state);
      params.append('sort', sortBy);
      params.append('limit', '100');
      const response = await fetch(`${BACKEND_URL}/api/actions?${params}`, { headers: getAuthHeader() });
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
        if (data.filters) setAvailableStates(data.filters.states || []);
      }
    } catch { toast.error('Erro ao carregar ações'); }
  };

  const loadPendingChanges = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/changes/pending`, { headers: getAuthHeader() });
      if (response.ok) {
        const data = await response.json();
        setHasUnpublishedChanges(data.has_pending);
        setPendingChangesCount(data.pending_count);
      }
    } catch {}
  };

  const loadScheduledActions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/scheduled`, { headers: getAuthHeader() });
      if (response.ok) {
        const data = await response.json();
        setScheduledActions(data.scheduled || []);
      }
    } catch {}
  };

  const loadNotifications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications?limit=20`, { headers: getAuthHeader() });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch {}
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/actions/stats/summary`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setStats(data.stats); }
    } catch {}
  };

  const loadMonthlyStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/actions/stats/monthly`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setMonthlyStats(data.data || []); }
    } catch {}
  };

  const loadTagStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/actions/stats/by-tag`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setTagStats(data.data || []); }
    } catch {}
  };

  const loadDonationStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/donations/stats`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setDonationStats(data.stats); }
    } catch {}
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/users`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setUsers(data.users || []); }
    } catch {}
  };

  const loadAuditLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/audit-logs?limit=100`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setAuditLogs(data.logs || []); }
    } catch {}
  };

  const loadSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings`, { headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); setSystemSettings(data); }
    } catch {}
  };

  useEffect(() => { if (isAuthenticated) loadActions(); }, [debouncedSearch, filters, sortBy, isAuthenticated]);

  const handleInputChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
  const toggleTag = (tag) => { setFormData(prev => ({ ...prev, tags: prev.tags.includes(tag) ? prev.tags.filter(t => t !== tag) : [...prev.tags, tag] })); };
  const addCustomTag = () => {
    if (formData.customTag.trim() && !formData.tags.includes(formData.customTag.trim().toUpperCase())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, prev.customTag.trim().toUpperCase()], customTag: '' }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    const CLOUDINARY_CLOUD_NAME = 'dnrowstdh';
    const CLOUDINARY_UPLOAD_PRESET = 'vivaimpact_uploads';
    
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const resourceType = isVideo ? 'video' : 'image';
      const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`;
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      
      try {
        setUploadProgress(30);
        const response = await fetch(uploadUrl, { method: 'POST', body: formDataUpload });
        setUploadProgress(80);
        
        if (response.ok) {
          const data = await response.json();
          const mediaUrl = data.secure_url;
          const thumbnail = isVideo ? data.secure_url.replace('/video/upload/', '/video/upload/c_thumb,w_400,h_300/').replace(/\.[^.]+$/, '.jpg') : mediaUrl;
          
          setMediaFiles(prev => [...prev, { 
            type: resourceType, 
            url: mediaUrl, 
            thumbnail: thumbnail, 
            public_id: data.public_id,
            isCover: prev.length === 0 // Primeira imagem é capa por padrão
          }]);
          setUploadProgress(100);
          setTimeout(() => setUploadProgress(0), 500);
          toast.success(`${isVideo ? 'Vídeo' : 'Imagem'} enviada para Cloudinary!`);
        } else { 
          const errorData = await response.json();
          throw new Error(errorData.error?.message || 'Erro no upload'); 
        }
      } catch (err) { 
        toast.error(err.message || 'Erro no upload'); 
        setUploadProgress(0); 
      }
    }
  };

  const removeMedia = (index) => {
    setMediaFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      // Se removeu a capa, definir primeira como capa
      if (prev[index]?.isCover && newFiles.length > 0) {
        newFiles[0] = { ...newFiles[0], isCover: true };
      }
      return newFiles;
    });
  };

  // Set cover photo
  const setCoverPhoto = (index) => {
    setMediaFiles(prev => prev.map((media, i) => ({
      ...media,
      isCover: i === index
    })));
  };

  // Parse video URL and get embed/thumbnail info
  const parseVideoUrl = (url) => {
    // YouTube patterns
    const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      return {
        type: 'youtube',
        videoId,
        embedUrl: `https://www.youtube.com/embed/${videoId}`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
        originalUrl: url
      };
    }

    // Vimeo patterns
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      const videoId = vimeoMatch[1];
      return {
        type: 'vimeo',
        videoId,
        embedUrl: `https://player.vimeo.com/video/${videoId}`,
        thumbnail: null,
        originalUrl: url
      };
    }

    // Google Drive patterns
    const driveRegex = /drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/;
    const driveMatch = url.match(driveRegex);
    if (driveMatch) {
      const fileId = driveMatch[1];
      return {
        type: 'drive',
        videoId: fileId,
        embedUrl: `https://drive.google.com/file/d/${fileId}/preview`,
        thumbnail: `https://drive.google.com/thumbnail?id=${fileId}`,
        originalUrl: url
      };
    }

    return null;
  };

  // Add video by link
  const handleAddVideoLink = () => {
    if (!videoLinkInput.trim()) return;

    const parsed = parseVideoUrl(videoLinkInput.trim());
    if (!parsed) {
      toast.error('Link de vídeo inválido. Use YouTube, Vimeo ou Google Drive.');
      return;
    }

    setMediaFiles(prev => [...prev, {
      type: 'video',
      url: parsed.embedUrl,
      thumbnail: parsed.thumbnail || '',
      videoType: parsed.type,
      originalUrl: parsed.originalUrl,
      isCover: prev.length === 0
    }]);

    setVideoLinkInput('');
    toast.success('Vídeo adicionado com sucesso!');
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    setMediaFiles(prev => {
      const newFiles = [...prev];
      const draggedItem = newFiles[draggedIndex];
      newFiles.splice(draggedIndex, 1);
      newFiles.splice(index, 0, draggedItem);
      return newFiles;
    });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSaveAction = async (e, saveAsDraft = false) => {
    e?.preventDefault();
    setLoading(true);
    try {
      let scheduledDateTime = null;
      if (formData.status === 'agendado' && formData.scheduled_date) {
        scheduledDateTime = new Date(`${formData.scheduled_date}T${formData.scheduled_time || '09:00'}`).toISOString();
      }
      const month = formData.month && formData.year ? `${formData.year}-${formData.month}` : '';
      const actionData = { ...formData, media: mediaFiles, id: editingAction?.id, scheduled_date: scheduledDateTime, month, status: saveAsDraft ? 'rascunho' : formData.status };
      const url = editingAction ? `${BACKEND_URL}/api/actions/${editingAction.id}` : `${BACKEND_URL}/api/actions`;
      const response = await fetch(url, { method: editingAction ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, body: JSON.stringify(actionData) });
      if (response.ok) {
        toast.success(saveAsDraft ? 'Rascunho salvo!' : (editingAction ? 'Ação atualizada!' : 'Ação criada!'));
        setHasUnpublishedChanges(true);
        resetForm();
        setActiveView('actions');
        loadAllData();
      } else { const error = await response.json(); throw new Error(error.detail || 'Erro'); }
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const handleDeleteAction = async (action) => {
    if (!window.confirm(`Excluir "${action.title}"?`)) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/actions/${action.id}`, { method: 'DELETE', headers: getAuthHeader() });
      if (response.ok) { toast.success('Ação excluída!'); setHasUnpublishedChanges(true); loadAllData(); }
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    let scheduledDate = '', scheduledTime = '';
    if (action.scheduled_date) { const dt = new Date(action.scheduled_date); scheduledDate = dt.toISOString().split('T')[0]; scheduledTime = dt.toTimeString().slice(0, 5); }
    let month = '', year = new Date().getFullYear().toString();
    if (action.month) { const parts = action.month.split('-'); if (parts.length === 2) { year = parts[0]; month = parts[1]; } }
    setFormData({
      title: action.title || '', subtitle: action.subtitle || 'Parceria Viva Impact e WG', location: action.location || '',
      beneficiaries: action.beneficiaries || '', description: action.description || '', tags: action.tags || [],
      urgency: action.urgency || 'normal', status: action.status || 'publicado', scheduled_date: scheduledDate,
      scheduled_time: scheduledTime, month, year, customTag: ''
    });
    // Garantir que a primeira imagem seja a capa se nenhuma estiver marcada
    const mediaWithCover = (action.media || []).map((media, index) => ({
      ...media,
      isCover: media.isCover !== undefined ? media.isCover : index === 0
    }));
    setMediaFiles(mediaWithCover);
    setVideoLinkInput('');
    setActiveView('form');
  };

  const handlePreviewAction = (action) => { setPreviewAction(action); setShowPreview(true); };
  const resetForm = () => {
    setFormData({ title: '', subtitle: 'Parceria Viva Impact e WG', location: '', beneficiaries: '', description: '', tags: [], urgency: 'normal', status: 'publicado', scheduled_date: '', scheduled_time: '', month: '', year: new Date().getFullYear().toString(), customTag: '' });
    setMediaFiles([]);
    setEditingAction(null);
    setVideoLinkInput('');
  };

  const handlePublish = async () => {
    if (!window.confirm('Publicar todas as alterações?')) return;
    setIsPublishing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/actions/publish`, { method: 'POST', headers: getAuthHeader() });
      if (response.ok) { const data = await response.json(); toast.success(data.message); setHasUnpublishedChanges(false); setPendingChangesCount(0); loadPendingChanges(); }
    } catch { toast.error('Erro ao publicar'); }
    finally { setIsPublishing(false); }
  };

  const handleGeneratePDF = async () => {
    setIsGeneratingPdf(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.state) params.append('state', filters.state);
      const response = await fetch(`${BACKEND_URL}/api/reports/pdf?${params}`, { headers: getAuthHeader() });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `relatorio_vivaimpact_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Relatório gerado!');
      }
    } catch { toast.error('Erro ao gerar relatório'); }
    finally { setIsGeneratingPdf(false); }
  };

  const handleMarkNotificationRead = async (id) => {
    await fetch(`${BACKEND_URL}/api/notifications/${id}/read`, { method: 'PUT', headers: getAuthHeader() });
    loadNotifications();
  };

  const handleMarkAllRead = async () => {
    await fetch(`${BACKEND_URL}/api/notifications/read-all`, { method: 'PUT', headers: getAuthHeader() });
    loadNotifications();
  };

  const handleExportLogs = async () => {
    const response = await fetch(`${BACKEND_URL}/api/audit-logs/export`, { headers: getAuthHeader() });
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Logs exportados!');
    }
  };

  const handleBackup = async () => {
    const response = await fetch(`${BACKEND_URL}/api/settings/backup`, { method: 'POST', headers: getAuthHeader() });
    if (response.ok) { toast.success('Backup criado!'); loadNotifications(); }
  };

  const clearFilters = () => { setFilters({ status: '', urgency: '', state: '' }); setSearchQuery(''); };
  const activeFiltersCount = useMemo(() => Object.values(filters).filter(v => v !== '').length, [filters]);
  const descriptionLength = formData.description?.length || 0;
  const isSuperAdmin = currentUser?.role === 'super_admin';

  if (authLoading) return <div className="min-h-screen bg-[#050B14] flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#22c55e] animate-spin" /></div>;
  if (!isAuthenticated) return <AdminLoginPremium onLoginSuccess={handleLoginSuccess} />;

  const SidebarItem = ({ icon: Icon, label, view, onClick, badge }) => (
    <motion.button whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }}
      onClick={onClick || (() => { setActiveView(view); if (view === 'users') loadUsers(); if (view === 'logs') loadAuditLogs(); if (view === 'settings') loadSettings(); })}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeView === view ? 'bg-[#22c55e]/10 text-[#22c55e] border-l-2 border-[#22c55e]' : 'text-slate-400 hover:text-white hover:bg-white/[0.03]'}`}>
      <Icon className="w-5 h-5" />
      {sidebarOpen && <span className="font-medium flex-1 text-left">{label}</span>}
      {badge && sidebarOpen && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded-full">{badge}</span>}
    </motion.button>
  );

  const StatCard = ({ icon: Icon, value, label, color }) => (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -4, scale: 1.02 }}
      className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6 hover:bg-white/[0.03] hover:border-white/10 transition-all">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-3xl font-bold text-white tracking-tight"><AnimatedCounter value={typeof value === 'number' ? value : 0} />{typeof value === 'string' && value.includes('+') ? '+' : ''}</p>
          <p className="text-slate-400 text-sm mt-1">{label}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}><Icon className="w-6 h-6" /></div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#050B14] flex">
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: '#0f172a', border: '1px solid rgba(34,197,94,0.3)', color: '#fff' } }} />

      <AnimatePresence>
        {showPreview && <PreviewModal action={previewAction} onClose={() => { setShowPreview(false); setPreviewAction(null); }} />}
        {showCreateUser && <CreateUserModal onClose={() => setShowCreateUser(false)} onSuccess={loadUsers} getAuthHeader={getAuthHeader} />}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside initial={{ x: -260 }} animate={{ x: 0 }}
        className={`fixed top-0 left-0 h-full bg-[#050B14]/80 backdrop-blur-2xl border-r border-white/[0.05] z-50 transition-all ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="p-6 border-b border-white/[0.05]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22c55e] to-[#16a34a] flex items-center justify-center shadow-[0_0_20px_rgba(34,197,94,0.3)]">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            {sidebarOpen && <div><h1 className="text-white font-bold">Viva Impact</h1><p className="text-slate-500 text-xs">Admin v3.5</p></div>}
          </div>
        </div>

        <nav className="p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-4 mb-3">Principal</p>
          <SidebarItem icon={LayoutDashboard} label="Dashboard" view="dashboard" />
          <SidebarItem icon={Package} label={`Ações Sociais ${actions.length > 0 ? `(${actions.length})` : ''}`} view="actions" badge={pendingChangesCount > 0 ? pendingChangesCount : null} />
          <SidebarItem icon={Plus} label="Nova Ação" view="form" onClick={() => { resetForm(); setActiveView('form'); }} />
          <SidebarItem icon={Inbox} label="Solicitações" view="donations" />
          
          {scheduledActions.length > 0 && <>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-4 mb-3 mt-6">Agendamentos</p>
            <SidebarItem icon={CalendarClock} label="Publicações" view="scheduled" badge={scheduledActions.length} />
          </>}

          <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold px-4 mb-3 mt-6">Ferramentas</p>
          <SidebarItem icon={FileDown} label="Relatórios" view="reports" />
          {isSuperAdmin && <>
            <SidebarItem icon={Users} label="Usuários" view="users" />
            <SidebarItem icon={FileSpreadsheet} label="Logs" view="logs" />
            <SidebarItem icon={Settings} label="Configurações" view="settings" />
          </>}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/[0.05]">
          <motion.button whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }} onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-all">
            <LogOut className="w-5 h-5" />{sidebarOpen && <span className="font-medium">Sair</span>}
          </motion.button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} min-h-screen transition-all`}>
        {/* Header */}
        <header className="sticky top-0 z-40 bg-[#050B14]/80 backdrop-blur-xl border-b border-white/[0.05] px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-slate-400 hover:text-white"><Menu className="w-6 h-6" /></button>
              <h2 className="text-xl font-bold text-white">
                {activeView === 'dashboard' && 'Dashboard'}
                {activeView === 'actions' && 'Ações Sociais'}
                {activeView === 'form' && (editingAction ? 'Editar Ação' : 'Nova Ação')}
                {activeView === 'scheduled' && 'Agendamentos'}
                {activeView === 'reports' && 'Relatórios'}
                {activeView === 'users' && 'Usuários'}
                {activeView === 'logs' && 'Logs de Auditoria'}
                {activeView === 'settings' && 'Configurações'}
                {activeView === 'donations' && 'Solicitações de Doação'}
              </h2>
            </div>

            <div className="flex items-center gap-4">
              {hasUnpublishedChanges && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full cursor-pointer hover:bg-amber-500/20"
                  onClick={() => setActiveView('actions')}>
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400 text-sm font-medium">{pendingChangesCount} alteraç{pendingChangesCount === 1 ? 'ão' : 'ões'} pendente{pendingChangesCount === 1 ? '' : 's'}</span>
                </motion.div>
              )}

              {/* Notifications Bell */}
              <div className="relative">
                <button onClick={() => setShowNotifications(!showNotifications)}
                  className="relative w-10 h-10 rounded-xl bg-white/[0.05] flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/[0.08] transition-all">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#22c55e] rounded-full flex items-center justify-center text-[10px] font-bold text-[#050B14]">{unreadCount}</span>
                  )}
                </button>
                <AnimatePresence>
                  {showNotifications && (
                    <NotificationsPanel notifications={notifications} unreadCount={unreadCount}
                      onMarkRead={handleMarkNotificationRead} onMarkAllRead={handleMarkAllRead}
                      onClose={() => setShowNotifications(false)} />
                  )}
                </AnimatePresence>
              </div>

              <button onClick={handleGeneratePDF} disabled={isGeneratingPdf}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.05] text-slate-300 border border-white/10 hover:bg-white/[0.08] transition-all">
                {isGeneratingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                <span className="hidden md:inline">PDF</span>
              </button>

              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={handlePublish} disabled={isPublishing}
                className={`flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold transition-all ${hasUnpublishedChanges ? 'bg-[#22c55e] text-[#050B14] hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] animate-pulse' : 'bg-white/[0.05] text-slate-400 border border-white/10'}`}>
                {isPublishing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Rocket className="w-5 h-5" />}
                <span>Publicar</span>
              </motion.button>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-8">
          <AnimatePresence mode="wait">
            {/* DASHBOARD */}
            {activeView === 'dashboard' && stats && (
              <motion.div key="dashboard" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatCard icon={Package} value={stats.total} label="Total de Ações" color="bg-emerald-500/10 text-emerald-400" />
                  <StatCard icon={Heart} value={stats.total_beneficiaries} label="Beneficiados" color="bg-blue-500/10 text-blue-400" />
                  <StatCard icon={MapPin} value={stats.cities || stats.regions} label="Cidades" color="bg-amber-500/10 text-amber-400" />
                  <StatCard icon={TrendingUp} value={stats.this_month} label="Este Mês" color="bg-purple-500/10 text-purple-400" />
                </div>

                {scheduledActions.length > 0 && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CalendarClock className="w-6 h-6 text-purple-400" />
                        <div>
                          <h3 className="text-white font-semibold">{scheduledActions.length} ação{scheduledActions.length > 1 ? 'ões' : ''} agendada{scheduledActions.length > 1 ? 's' : ''}</h3>
                          <p className="text-purple-300 text-sm">Próxima: {scheduledActions[0]?.title} - {scheduledActions[0]?.countdown_human}</p>
                        </div>
                      </div>
                      <button onClick={() => setActiveView('scheduled')} className="px-4 py-2 bg-purple-500/20 text-purple-300 rounded-lg hover:bg-purple-500/30">Ver todas</button>
                    </div>
                  </div>
                )}

                {/* Card de Solicitações de Doação */}
                {donationStats && (donationStats.pending > 0 || donationStats.urgent > 0) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {donationStats.pending > 0 && (
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setActiveView('donations')}
                        className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 cursor-pointer hover:bg-amber-500/15 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                            <Inbox className="w-6 h-6 text-amber-400" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-amber-400">{donationStats.pending}</p>
                            <p className="text-amber-300 text-sm">Solicitações Pendentes</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                    {donationStats.urgent > 0 && (
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        onClick={() => setActiveView('donations')}
                        className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 cursor-pointer hover:bg-red-500/15 transition-all animate-pulse"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                            <AlertTriangle className="w-6 h-6 text-red-400" />
                          </div>
                          <div>
                            <p className="text-3xl font-bold text-red-400">{donationStats.urgent}</p>
                            <p className="text-red-300 text-sm">Solicitações Urgentes</p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-6"><BarChart3 className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Ações por Mês</h3></div>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={monthlyStats}>
                        <defs><linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/><stop offset="95%" stopColor="#22c55e" stopOpacity={0}/></linearGradient></defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="count" stroke="#22c55e" fillOpacity={1} fill="url(#colorCount)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-6"><PieChart className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Por Categoria</h3></div>
                    <ResponsiveContainer width="100%" height={250}>
                      <RePieChart>
                        <Pie data={tagStats} dataKey="count" nameKey="tag" cx="50%" cy="50%" outerRadius={80} innerRadius={50}>
                          {tagStats.map((_, index) => <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </RePieChart>
                    </ResponsiveContainer>
                    <div className="flex flex-wrap gap-2 mt-4 justify-center">
                      {tagStats.slice(0, 5).map((item, index) => (
                        <span key={item.tag} className="flex items-center gap-2 text-xs text-slate-400">
                          <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[index % CHART_COLORS.length] }} />{item.tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-6"><Activity className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Ações Recentes</h3></div>
                  <div className="space-y-3">
                    {stats.recent_actions?.slice(0, 5).map((action, index) => (
                      <div key={action.id || index} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.02] hover:bg-white/[0.04] transition-all cursor-pointer" onClick={() => handlePreviewAction(action)}>
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#22c55e]/10 flex items-center justify-center">
                            {action.media?.[0]?.thumbnail || action.media?.[0]?.url ? <img src={action.media[0].thumbnail || action.media[0].url} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 text-[#22c55e]" />}
                          </div>
                          <div>
                            <p className="text-white font-medium">{action.title}</p>
                            <p className="text-slate-500 text-sm flex items-center gap-1"><MapPin className="w-3 h-3" /> {action.location}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${URGENCY_CONFIG[action.urgency]?.bg} ${URGENCY_CONFIG[action.urgency]?.text}`}>{URGENCY_CONFIG[action.urgency]?.label || 'Normal'}</span>
                          <Eye className="w-4 h-4 text-slate-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* ACTIONS LIST */}
            {activeView === 'actions' && (
              <motion.div key="actions" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  <div className="flex items-center gap-2 px-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded-xl flex-1 max-w-md">
                    <Search className="w-5 h-5 text-slate-500" />
                    <input type="text" placeholder="Buscar ações..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-none outline-none text-white placeholder-slate-500 flex-1" />
                    {searchQuery && <button onClick={() => setSearchQuery('')} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>}
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      className="px-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                      <option value="">Todos Status</option>
                      <option value="publicado">Publicado</option>
                      <option value="rascunho">Rascunho</option>
                      <option value="agendado">Agendado</option>
                    </select>

                    <select value={filters.urgency} onChange={(e) => setFilters(prev => ({ ...prev, urgency: e.target.value }))}
                      className="px-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                      <option value="">Todas Urgências</option>
                      <option value="baixa">Baixa</option>
                      <option value="normal">Normal</option>
                      <option value="alta">Alta</option>
                      <option value="urgente">Urgente</option>
                    </select>

                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                      className="px-4 py-3 bg-white/[0.02] border border-white/[0.05] rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                      <option value="recent">Mais Recentes</option>
                      <option value="oldest">Mais Antigas</option>
                      <option value="urgency">Por Urgência</option>
                    </select>

                    <div className="flex bg-white/[0.02] rounded-xl p-1 border border-white/[0.05]">
                      <button onClick={() => setViewMode('grid')} className={`px-3 py-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'text-slate-400 hover:text-white'}`}><Grid className="w-5 h-5" /></button>
                      <button onClick={() => setViewMode('list')} className={`px-3 py-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#22c55e]/20 text-[#22c55e]' : 'text-slate-400 hover:text-white'}`}><List className="w-5 h-5" /></button>
                    </div>
                  </div>
                </div>

                {activeFiltersCount > 0 && (
                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="text-slate-500 text-sm">Filtros ativos:</span>
                    {Object.entries(filters).filter(([_, v]) => v).map(([key, value]) => (
                      <span key={key} className="flex items-center gap-1 px-3 py-1 bg-white/[0.05] rounded-full text-sm text-white">
                        {key}: {value}<button onClick={() => setFilters(prev => ({ ...prev, [key]: '' }))} className="ml-1 text-slate-400 hover:text-white"><X className="w-3 h-3" /></button>
                      </span>
                    ))}
                    <button onClick={clearFilters} className="text-[#22c55e] text-sm hover:underline font-medium">Limpar</button>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <p className="text-slate-400 text-sm">{actions.length} ação{actions.length !== 1 ? 'ões' : ''}</p>
                  <button onClick={loadActions} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm"><RefreshCw className="w-4 h-4" />Atualizar</button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-[#22c55e] animate-spin" /></div>
                ) : actions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                    <Package className="w-16 h-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">Nenhuma ação encontrada</p>
                    <button onClick={() => { resetForm(); setActiveView('form'); }} className="mt-4 px-6 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl"><Plus className="w-5 h-5 inline mr-2" />Criar</button>
                  </div>
                ) : viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {actions.map((action, index) => (
                      <motion.div key={action.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }} whileHover={{ y: -4 }}
                        className="group bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl overflow-hidden hover:bg-white/[0.03] hover:border-white/10 transition-all">
                        <div className="relative aspect-video overflow-hidden">
                          {action.media?.[0] ? (
                            action.media[0].type === 'video' ? (
                              <div className="w-full h-full bg-slate-900 flex items-center justify-center relative">
                                <Video className="w-10 h-10 text-slate-600" />
                                {action.media[0].thumbnail && <img src={action.media[0].thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                              </div>
                            ) : <img src={action.media[0].thumbnail || action.media[0].url} alt={action.title} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-[#22c55e]/10 to-transparent flex items-center justify-center"><Image className="w-10 h-10 text-slate-600" /></div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-end p-4 gap-2">
                            <button onClick={() => handlePreviewAction(action)} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30"><Eye className="w-4 h-4" /></button>
                            <button onClick={() => handleEditAction(action)} className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handleDeleteAction(action)} className="w-10 h-10 rounded-full bg-red-500/20 backdrop-blur-md flex items-center justify-center text-red-400 hover:bg-red-500/30"><Trash2 className="w-4 h-4" /></button>
                          </div>
                          {action.status === 'agendado' && <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-purple-500/80 backdrop-blur-sm rounded-full text-white text-xs"><Clock className="w-3 h-3" />{action.countdown_human || 'Agendado'}</div>}
                        </div>
                        <div className="p-5 space-y-3">
                          <div className="flex gap-2">
                            <span className={`px-2 py-1 rounded-md text-[10px] font-semibold ${STATUS_CONFIG[action.status]?.bg} ${STATUS_CONFIG[action.status]?.text}`}>{STATUS_CONFIG[action.status]?.label || 'Publicado'}</span>
                            <span className={`px-2 py-1 rounded-md text-[10px] font-semibold flex items-center gap-1 ${URGENCY_CONFIG[action.urgency]?.bg} ${URGENCY_CONFIG[action.urgency]?.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${URGENCY_CONFIG[action.urgency]?.dot}`}></span>{URGENCY_CONFIG[action.urgency]?.label || 'Normal'}
                            </span>
                          </div>
                          <h3 className="text-white font-semibold line-clamp-2">{action.title}</h3>
                          <div className="space-y-1.5">
                            <p className="text-slate-400 text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-500" /> {action.location}</p>
                            <p className="text-slate-400 text-sm flex items-center gap-2"><Users className="w-4 h-4 text-slate-500" /> {action.beneficiaries}</p>
                          </div>
                          {action.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 pt-2">
                              {action.tags.slice(0, 3).map(tag => <span key={tag} className="px-2 py-0.5 bg-white/[0.05] rounded text-[10px] text-slate-400">{tag}</span>)}
                              {action.tags.length > 3 && <span className="px-2 py-0.5 bg-white/[0.05] rounded text-[10px] text-slate-500">+{action.tags.length - 3}</span>}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {actions.map((action, index) => (
                      <motion.div key={action.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.02 }}
                        className="flex items-center gap-4 p-4 bg-white/[0.02] border border-white/[0.05] rounded-xl hover:bg-white/[0.03] transition-all">
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-900 flex-shrink-0">
                          {action.media?.[0] ? <img src={action.media[0].thumbnail || action.media[0].url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#22c55e]/10 flex items-center justify-center"><Image className="w-6 h-6 text-slate-600" /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-semibold truncate">{action.title}</h3>
                          <p className="text-slate-400 text-sm flex items-center gap-2"><MapPin className="w-3 h-3" /> {action.location} • {action.beneficiaries}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${STATUS_CONFIG[action.status]?.bg} ${STATUS_CONFIG[action.status]?.text}`}>{STATUS_CONFIG[action.status]?.label}</span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${URGENCY_CONFIG[action.urgency]?.bg} ${URGENCY_CONFIG[action.urgency]?.text}`}>{URGENCY_CONFIG[action.urgency]?.label}</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => handlePreviewAction(action)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => handleEditAction(action)} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDeleteAction(action)} className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/20"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* FORM */}
            {activeView === 'form' && (
              <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-6xl">
                <form onSubmit={handleSaveAction} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column */}
                  <div className="space-y-6">
                    <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/[0.05]">
                        <FileText className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Informações Básicas</h3>
                      </div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Título *</label>
                          <input type="text" name="title" value={formData.title} onChange={handleInputChange} placeholder="Ex: Dia das Crianças 2026" required
                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
                        </div>
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Subtítulo</label>
                          <input type="text" name="subtitle" value={formData.subtitle} onChange={handleInputChange}
                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Mês</label>
                            <select name="month" value={formData.month} onChange={handleInputChange} className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                              <option value="">Selecione</option>
                              {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Ano</label>
                            <select name="year" value={formData.year} onChange={handleInputChange} className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Localização *</label>
                            <div className="relative">
                              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                              <input type="text" name="location" value={formData.location} onChange={handleInputChange} placeholder="Cidade - Estado" required
                                className="w-full pl-12 pr-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Beneficiários *</label>
                            <div className="relative">
                              <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                              <input type="text" name="beneficiaries" value={formData.beneficiaries} onChange={handleInputChange} placeholder="Ex: 200+ crianças" required
                                className="w-full pl-12 pr-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none" />
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Descrição *</label>
                            <span className={`text-xs ${descriptionLength > 5000 ? 'text-red-400' : 'text-slate-500'}`}>{descriptionLength}/5000</span>
                          </div>
                          <textarea name="description" value={formData.description} onChange={handleInputChange} placeholder="Descreva a ação..." rows="6" required
                            className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none resize-none" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/[0.05]"><Settings className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Status e Urgência</h3></div>
                      <div className="space-y-4">
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Status</label>
                          <select name="status" value={formData.status} onChange={handleInputChange} className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                            <option value="rascunho">Rascunho</option>
                            <option value="publicado">Publicado</option>
                            <option value="agendado">Agendado</option>
                          </select>
                        </div>
                        {formData.status === 'agendado' && (
                          <div className="grid grid-cols-2 gap-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                            <div>
                              <label className="text-xs uppercase tracking-wider text-purple-300 font-semibold block mb-2">Data *</label>
                              <input type="date" name="scheduled_date" value={formData.scheduled_date} onChange={handleInputChange} required={formData.status === 'agendado'} min={new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-3 bg-white/[0.02] border border-purple-500/30 rounded-xl text-white focus:border-purple-500 focus:outline-none" />
                            </div>
                            <div>
                              <label className="text-xs uppercase tracking-wider text-purple-300 font-semibold block mb-2">Hora</label>
                              <input type="time" name="scheduled_time" value={formData.scheduled_time} onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-white/[0.02] border border-purple-500/30 rounded-xl text-white focus:border-purple-500 focus:outline-none" />
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold block mb-2">Urgência</label>
                          <div className="flex gap-2">
                            {Object.entries(URGENCY_CONFIG).map(([key, config]) => (
                              <button key={key} type="button" onClick={() => setFormData(prev => ({ ...prev, urgency: key }))}
                                className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${formData.urgency === key ? `${config.bg} ${config.text} border ${config.border}` : 'bg-white/[0.02] text-slate-400 border border-white/10 hover:bg-white/[0.04]'}`}>
                                <span className={`w-2 h-2 rounded-full ${config.dot}`}></span>{config.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/[0.05]"><Image className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Mídia</h3></div>
                      
                      {/* Upload de arquivos */}
                      <label className="block cursor-pointer">
                        <input type="file" accept="image/*,video/mp4,video/webm" onChange={handleImageUpload} multiple className="hidden" />
                        <div className="border-2 border-dashed border-[#22c55e]/30 rounded-xl p-8 text-center hover:border-[#22c55e]/50 hover:bg-[#22c55e]/5 transition-all">
                          <Upload className="w-10 h-10 text-[#22c55e] mx-auto mb-3" />
                          <p className="text-white font-medium">Arraste ou clique para enviar</p>
                          <p className="text-slate-500 text-sm mt-1">JPG, PNG, GIF, MP4, WebM (máx. 10MB)</p>
                        </div>
                      </label>
                      {uploadProgress > 0 && uploadProgress < 100 && <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden"><motion.div initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} className="h-full bg-[#22c55e]" /></div>}
                      
                      {/* Galeria de fotos */}
                      {mediaFiles.filter(m => m.type === 'image' || (m.type === 'video' && !m.videoType)).length > 0 && (
                        <div className="mt-6">
                          <div className="flex items-center gap-2 mb-3">
                            <Image className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-400 text-sm">Galeria de Fotos</span>
                            <span className="text-slate-600 text-xs">(clique para definir como capa)</span>
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            {mediaFiles
                              .map((media, originalIndex) => ({ media, originalIndex }))
                              .filter(({ media }) => media.type === 'image' || (media.type === 'video' && !media.videoType))
                              .map(({ media, originalIndex }) => (
                              <div 
                                key={originalIndex} 
                                className={`relative aspect-square rounded-xl overflow-hidden group cursor-pointer transition-all ${media.isCover ? 'ring-2 ring-[#22c55e] shadow-[0_0_15px_rgba(34,197,94,0.4)]' : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, originalIndex)}
                                onDragOver={(e) => handleDragOver(e, originalIndex)}
                                onDragEnd={handleDragEnd}
                                onClick={() => setCoverPhoto(originalIndex)}
                              >
                                <div className="absolute top-2 left-2 w-6 h-6 rounded bg-black/50 flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 z-10 transition-opacity">
                                  <GripVertical className="w-3 h-3 text-white" />
                                </div>
                                {media.type === 'video' ? (
                                  <div className="w-full h-full bg-slate-900 flex items-center justify-center">
                                    <Video className="w-8 h-8 text-slate-600" />
                                    {media.thumbnail && <img src={media.thumbnail} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                                  </div>
                                ) : (
                                  <img src={media.url} alt="" className="w-full h-full object-cover" />
                                )}
                                <button type="button" onClick={(e) => { e.stopPropagation(); removeMedia(originalIndex); }} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity z-10"><X className="w-4 h-4" /></button>
                                {media.isCover && (
                                  <span className="absolute bottom-2 left-2 px-2 py-1 bg-[#22c55e] text-[10px] font-semibold text-[#050B14] rounded flex items-center gap-1">
                                    <Star className="w-3 h-3" /> Capa
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Seção de Vídeos por Link */}
                      <div className="mt-6 pt-6 border-t border-white/[0.05]">
                        <div className="flex items-center gap-2 mb-4">
                          <Youtube className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-400 text-sm">Vídeos por Link</span>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl focus-within:border-[#22c55e]/50 transition-all">
                            <Link className="w-4 h-4 text-slate-500" />
                            <input
                              type="text"
                              value={videoLinkInput}
                              onChange={(e) => setVideoLinkInput(e.target.value)}
                              placeholder="Cole o link do YouTube, Google Drive ou Vimeo..."
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddVideoLink())}
                              className="flex-1 bg-transparent border-none outline-none text-white placeholder-slate-500 text-sm"
                              data-testid="video-link-input"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={handleAddVideoLink}
                            className="px-4 py-3 bg-[#22c55e]/20 text-[#22c55e] rounded-xl hover:bg-[#22c55e]/30 transition-all flex items-center gap-2"
                            data-testid="add-video-btn"
                          >
                            <Plus className="w-4 h-4" />
                            <span className="text-sm font-medium">Adicionar</span>
                          </button>
                        </div>
                        
                        {/* Lista de vídeos adicionados */}
                        {mediaFiles.filter(m => m.type === 'video' && m.videoType).length > 0 && (
                          <div className="mt-4 space-y-3">
                            {mediaFiles
                              .map((media, originalIndex) => ({ media, originalIndex }))
                              .filter(({ media }) => media.type === 'video' && media.videoType)
                              .map(({ media, originalIndex }) => (
                              <div key={originalIndex} className="flex items-center gap-4 p-3 bg-white/[0.02] border border-white/10 rounded-xl">
                                <div className="relative w-24 h-14 rounded-lg overflow-hidden bg-slate-900 flex-shrink-0">
                                  {media.thumbnail ? (
                                    <img src={media.thumbnail} alt="Video thumbnail" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Play className="w-6 h-6 text-slate-600" />
                                    </div>
                                  )}
                                  <span className={`absolute bottom-1 right-1 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${
                                    media.videoType === 'youtube' ? 'bg-red-500 text-white' :
                                    media.videoType === 'vimeo' ? 'bg-cyan-500 text-white' :
                                    'bg-blue-500 text-white'
                                  }`}>
                                    {media.videoType}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-slate-400 text-xs truncate">{media.originalUrl}</p>
                                </div>
                                <button 
                                  type="button"
                                  onClick={() => removeMedia(originalIndex)}
                                  className="w-8 h-8 rounded-lg bg-red-500/10 text-red-400 flex items-center justify-center hover:bg-red-500/20 transition-all"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/[0.05]"><Tag className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Tags</h3></div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {PREDEFINED_TAGS.map(tag => (
                          <button key={tag} type="button" onClick={() => toggleTag(tag)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${formData.tags.includes(tag) ? 'bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/50' : 'bg-white/[0.02] text-slate-400 border border-white/10 hover:bg-white/[0.04]'}`}>
                            {tag}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input type="text" name="customTag" value={formData.customTag} onChange={handleInputChange} placeholder="Tag personalizada..."
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomTag())}
                          className="flex-1 px-4 py-2 bg-white/[0.02] border border-white/10 rounded-lg text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none text-sm" />
                        <button type="button" onClick={addCustomTag} className="px-4 py-2 bg-[#22c55e]/20 text-[#22c55e] rounded-lg hover:bg-[#22c55e]/30"><Plus className="w-4 h-4" /></button>
                      </div>
                      {formData.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/[0.05]">
                          <span className="text-slate-500 text-xs">Selecionadas:</span>
                          {formData.tags.map(tag => (
                            <span key={tag} className="flex items-center gap-1 px-2 py-1 bg-[#22c55e]/10 text-[#22c55e] text-xs rounded-lg">
                              {tag}<button type="button" onClick={() => toggleTag(tag)} className="hover:text-white"><X className="w-3 h-3" /></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-4">
                      <button type="button" onClick={() => { setPreviewAction({ ...formData, media: mediaFiles, id: editingAction?.id || 'preview' }); setShowPreview(true); }}
                        className="flex items-center gap-2 px-6 py-3 bg-white/[0.05] text-slate-300 font-semibold rounded-xl border border-white/10 hover:bg-white/[0.08]">
                        <Eye className="w-5 h-5" /> Preview
                      </button>
                      <button type="button" onClick={(e) => handleSaveAction(e, true)}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-600/20 text-slate-300 font-semibold rounded-xl border border-slate-500/30 hover:bg-slate-600/30">
                        <FileText className="w-5 h-5" /> Rascunho
                      </button>
                      <button type="submit" disabled={loading}
                        className="flex-1 flex items-center justify-center gap-2 px-8 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] disabled:opacity-50">
                        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Salvando...</> : <><Save className="w-5 h-5" /> {editingAction ? 'Atualizar' : 'Publicar'}</>}
                      </button>
                    </div>
                  </div>
                </form>
              </motion.div>
            )}

            {/* SCHEDULED */}
            {activeView === 'scheduled' && (
              <motion.div key="scheduled" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-6"><CalendarClock className="w-5 h-5 text-purple-400" /><h3 className="text-white font-semibold">Publicações Agendadas ({scheduledActions.length})</h3></div>
                  {scheduledActions.length === 0 ? (
                    <div className="text-center py-12"><CalendarClock className="w-16 h-16 text-slate-600 mx-auto mb-4" /><p className="text-slate-400">Nenhuma publicação agendada</p></div>
                  ) : (
                    <div className="space-y-4">
                      {scheduledActions.map((action, index) => (
                        <motion.div key={action.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.05 }}
                          className="flex items-center justify-between p-5 rounded-xl bg-purple-500/5 border border-purple-500/20 hover:bg-purple-500/10 transition-all">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center"><Clock className="w-6 h-6 text-purple-400" /></div>
                            <div>
                              <p className="text-white font-semibold">{action.title}</p>
                              <p className="text-slate-400 text-sm">{action.location} • {action.beneficiaries}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-purple-400 font-semibold">{action.countdown_human}</p>
                            <p className="text-slate-500 text-sm">{new Date(action.scheduled_date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleEditAction(action)} className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20"><Edit className="w-4 h-4" /></button>
                            <button onClick={() => handlePreviewAction(action)} className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20"><Eye className="w-4 h-4" /></button>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* USERS */}
            {activeView === 'users' && isSuperAdmin && (
              <motion.div key="users" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">Gerenciar Usuários</h3>
                  <button onClick={() => setShowCreateUser(true)} className="flex items-center gap-2 px-4 py-2 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)]">
                    <UserPlus className="w-4 h-4" /> Novo Usuário
                  </button>
                </div>
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/[0.05]">
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Usuário</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Email</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Role</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Status</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Último Acesso</th>
                        <th className="px-6 py-4 text-right text-xs uppercase tracking-wider text-slate-400 font-semibold">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                          <td className="px-6 py-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-full bg-[#22c55e]/10 flex items-center justify-center"><span className="text-[#22c55e] font-bold">{user.username[0].toUpperCase()}</span></div><span className="text-white font-medium">{user.username}</span></div></td>
                          <td className="px-6 py-4 text-slate-400">{user.email}</td>
                          <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs font-medium ${ROLE_CONFIG[user.role]?.bg} ${ROLE_CONFIG[user.role]?.text}`}>{ROLE_CONFIG[user.role]?.label || user.role}</span></td>
                          <td className="px-6 py-4">{user.is_active ? <span className="flex items-center gap-1 text-emerald-400 text-sm"><CheckCircle className="w-4 h-4" />Ativo</span> : <span className="flex items-center gap-1 text-red-400 text-sm"><AlertCircle className="w-4 h-4" />Inativo</span>}</td>
                          <td className="px-6 py-4 text-slate-400 text-sm">{user.last_login ? new Date(user.last_login).toLocaleString('pt-BR') : 'Nunca'}</td>
                          <td className="px-6 py-4 text-right">
                            {user.username !== 'admin' && (
                              <div className="flex gap-2 justify-end">
                                <button onClick={async () => {
                                  await fetch(`${BACKEND_URL}/api/users/${user.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeader() }, body: JSON.stringify({ is_active: !user.is_active }) });
                                  loadUsers(); toast.success('Usuário atualizado');
                                }} className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/20">{user.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}</button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* LOGS */}
            {activeView === 'logs' && isSuperAdmin && (
              <motion.div key="logs" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold">Logs de Auditoria</h3>
                  <button onClick={handleExportLogs} className="flex items-center gap-2 px-4 py-2 bg-white/[0.05] text-slate-300 rounded-xl border border-white/10 hover:bg-white/[0.08]">
                    <Download className="w-4 h-4" /> Exportar CSV
                  </button>
                </div>
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/[0.05]">
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Data/Hora</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Usuário</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Ação</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">Recurso</th>
                        <th className="px-6 py-4 text-left text-xs uppercase tracking-wider text-slate-400 font-semibold">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="border-b border-white/[0.05] hover:bg-white/[0.02]">
                          <td className="px-6 py-4 text-slate-400 text-sm">{new Date(log.timestamp).toLocaleString('pt-BR')}</td>
                          <td className="px-6 py-4 text-white">{log.user}</td>
                          <td className="px-6 py-4"><span className={`px-2 py-1 rounded text-xs font-medium ${log.action === 'DELETE' ? 'bg-red-500/10 text-red-400' : log.action === 'CREATE' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-blue-500/10 text-blue-400'}`}>{log.action}</span></td>
                          <td className="px-6 py-4 text-slate-400">{log.resource_type}</td>
                          <td className="px-6 py-4 text-slate-500 text-sm">{log.ip_address || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            )}

            {/* SETTINGS */}
            {activeView === 'settings' && isSuperAdmin && systemSettings && (
              <motion.div key="settings" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                {/* System Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
                  <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-4"><Shield className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Status do Sistema</h3></div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl">
                        <span className="text-slate-400 text-sm">MongoDB</span>
                        <span className={`text-sm font-medium ${systemSettings.status?.mongodb === 'connected' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {systemSettings.status?.mongodb === 'connected' ? '✓ Conectado' : '✗ Desconectado'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white/[0.02] rounded-xl">
                        <span className="text-slate-400 text-sm">Cloudinary</span>
                        <span className={`text-sm font-medium ${systemSettings.status?.cloudinary === 'configured' ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {systemSettings.status?.cloudinary === 'configured' ? '✓ Configurado' : '⚠ Fallback'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-4"><Database className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Backup</h3></div>
                    <p className="text-slate-400 text-sm mb-4">Crie um backup manual do banco de dados.</p>
                    <button onClick={handleBackup} className="w-full px-3 py-2 text-sm bg-blue-500/20 text-blue-400 font-semibold rounded-xl border border-blue-500/30 hover:bg-blue-500/30">
                      <Database className="w-4 h-4 inline mr-2" /> Backup Agora
                    </button>
                  </div>
                </div>

                {/* Security Settings Panel */}
                <div className="max-w-4xl">
                  <SecuritySettingsPanel token={localStorage.getItem('viva_admin_token')} />
                </div>

                {/* Security Dashboard */}
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                  <SecurityDashboard token={localStorage.getItem('viva_admin_token')} />
                </div>
              </motion.div>
            )}

            {/* REPORTS */}
            {activeView === 'reports' && (
              <motion.div key="reports" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-2xl">
                <div className="bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-6"><FileDown className="w-5 h-5 text-[#22c55e]" /><h3 className="text-white font-semibold">Gerar Relatório PDF</h3></div>
                  <p className="text-slate-400 mb-6">Selecione os filtros para gerar um relatório.</p>
                  <div className="space-y-4 mb-6">
                    <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                      <option value="">Todos os Status</option>
                      <option value="publicado">Publicado</option>
                      <option value="rascunho">Rascunho</option>
                      <option value="agendado">Agendado</option>
                    </select>
                    <select value={filters.state} onChange={(e) => setFilters(prev => ({ ...prev, state: e.target.value }))} className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none">
                      <option value="">Todos os Estados</option>
                      {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <button onClick={handleGeneratePDF} disabled={isGeneratingPdf} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#22c55e] text-[#050B14] font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] disabled:opacity-50">
                    {isGeneratingPdf ? <><Loader2 className="w-5 h-5 animate-spin" /> Gerando...</> : <><Download className="w-5 h-5" /> Baixar PDF</>}
                  </button>
                </div>
              </motion.div>
            )}

            {/* DONATIONS */}
            {activeView === 'donations' && (
              <motion.div key="donations" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <AdminDonations token={localStorage.getItem('viva_admin_token')} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPremium;
